import React from "react"
import { Box, Button, Stack, Typography } from "@mui/material"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew"
import MotionPhotosAutoIcon from "@mui/icons-material/MotionPhotosAuto"
import FileDownloadOffOutlinedIcon from "@mui/icons-material/FileDownloadOffOutlined"
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase"
import LayersClearIcon from "@mui/icons-material/LayersClear"
import Rotate90DegreesCcwIcon from "@mui/icons-material/Rotate90DegreesCcw"
import ClearAllIcon from "@mui/icons-material/ClearAll"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded"
import TuneRoundedIcon from "@mui/icons-material/TuneRounded"
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined"

const operationColors = {
  TMNL_LOCK: ["#b76c08", "#fff2d6"],
  TMNL_UNLOCK: ["#16824b", "#ddf6e8"],
  TMNL_RESTART: ["#236bd1", "#e4efff"],
  TMNL_SHUTDOWN: ["#d03343", "#ffe6e9"],
  AUTO_SETTLEMENT: ["#6743c6", "#eee7ff"],
  UNINSTALL_PAYT_APP: ["#d45325", "#ffebdf"],
  UNINSTALL_APP: ["#bf3680", "#fce3f0"],
  CLEAR_PAYT_APP_DATA: ["#078a98", "#ddf5f7"],
  CLEAR_REVERSAL_DATA: ["#c17a0a", "#fff1d5"],
  CLEAR_BATCH_DATA: ["#4d61c8", "#e8ecff"],
}
const groups = [
  { title: "Device controls", color: "#176b70", tint: "#eaf5f4", actions: [
    ["Lock", "TMNL_LOCK", LockOutlinedIcon],
    ["Unlock", "TMNL_UNLOCK", LockOpenOutlinedIcon],
    ["Restart", "TMNL_RESTART", RestartAltIcon],
    ["Power off", "TMNL_SHUTDOWN", PowerSettingsNewIcon, false, true],
  ] },
  { title: "Applications & settlement", color: "#4956a2", tint: "#eef0fb", actions: [
    ["Auto settlement", "AUTO_SETTLEMENT", MotionPhotosAutoIcon, true],
    ["Uninstall payment app", "UNINSTALL_PAYT_APP", FileDownloadOffOutlinedIcon, true, true],
    ["Uninstall app", "UNINSTALL_APP", PhonelinkEraseIcon, false, true],
  ] },
  { title: "Data maintenance", color: "#936121", tint: "#fcf3e5", actions: [
    ["Clear payt. app data", "CLEAR_PAYT_APP_DATA", LayersClearIcon, true],
    ["Clear reversal data", "CLEAR_REVERSAL_DATA", Rotate90DegreesCcwIcon, true],
    ["Clear batch", "CLEAR_BATCH_DATA", ClearAllIcon, true],
  ] },
]

export default function TerminalOperations({ model, isSubmit, onOperation, onViewStatus }) {
  return (
    <Box component="section" aria-labelledby="terminal-operations-title" sx={{ borderTop: "1px solid #dce7e7", bgcolor: "#f8fafc" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}
        sx={{ px: { xs: 2, md: 2.5 }, py: 1.75 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ display: "flex", p: 1.25, bgcolor: "#116c6f", color: "white", borderRadius: 2, boxShadow: "0 3px 8px #116c6f22" }}><TuneRoundedIcon /></Box>
          <Box>
            <Typography id="terminal-operations-title" component="h3" variant="subtitle1" sx={{ fontWeight: 700, color: "#173f42" }}>Terminal operations</Typography>

          </Box>
        </Stack>
        {/* <Button onClick={onViewStatus} startIcon={<HistoryRoundedIcon />} endIcon={<ArrowForwardRoundedIcon />} sx={{ textTransform: "none", color: "#116c6f", fontWeight: 600, bgcolor: "white", border: "1px solid #cee2e1", borderRadius: 2, px: 1.5, whiteSpace: "nowrap", "&:hover": { bgcolor: "#e3f1ef", borderColor: "#91bfbc" } }}>View operation status</Button> */}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2, px: { xs: 2, md: 2.5 }, pb: 2 }}>
        {groups.map((group) => <Box key={group.title} role="group" aria-label={group.title} sx={{ minWidth: 0, p: { xs: 1.5, lg: 2 }, borderRadius: 3, background: `linear-gradient(145deg, ${group.tint}, #f8fafc 85%)`, border: `1px solid ${group.color}20` }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ width: 3, height: 18, bgcolor: group.color, borderRadius: 2 }} />
            <Box>
              <Typography component="h4" sx={{ fontSize: 13, fontWeight: 700, color: "#243e48", lineHeight: 1.5 }}>{group.title}</Typography>

            </Box>
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: group.actions.length === 4 ? { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" } : "repeat(3, minmax(0, 1fr))", gap: { xs: 0.25, lg: 0.75 } }}>
            {group.actions.map(([label, value, Icon, passwordRequired, destructive]) => {
              const unsupported = model === "ME60" && ["UNINSTALL_APP", "UNINSTALL_PAYT_APP"].includes(value)
              const protectedAction = passwordRequired || value === "UNINSTALL_APP"
              const [color, iconBackground] = operationColors[value]
              return <Button key={value} aria-label={label} title={unsupported ? "Unavailable for ME60 terminals" : label} disabled={isSubmit || unsupported}
                onClick={() => onOperation(label, value, passwordRequired)}
                sx={{ minWidth: 0, textTransform: "none", flexDirection: "column", justifyContent: "flex-start", textAlign: "center", gap: 1.25, px: 0.5, py: 1, borderRadius: 2.5, bgcolor: "transparent", color: "#253e49", transition: "background-color 160ms", "&:hover": { bgcolor: destructive ? "#fceeed80" : `${group.color}0c`, "& .operation-icon": { transform: "translateY(-3px)", boxShadow: `0 7px 16px ${color}25` } }, "&.Mui-focusVisible": { outline: `2px solid ${color}`, outlineOffset: 2 }, "&.Mui-disabled": { opacity: 0.45 }, "@media (prefers-reduced-motion: reduce)": { transition: "none", "& .operation-icon": { transition: "none", transform: "none !important" } } }}>
                <Box component="span" className="operation-icon" sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, width: 56, height: 56, borderRadius: "18px", bgcolor: iconBackground, color, boxShadow: `0 3px 9px ${color}14`, transition: "transform 160ms, box-shadow 160ms" }}>
                  <Icon sx={{ fontSize: 32 }} />
                  {protectedAction && <Box component="span" title="Password confirmation required" sx={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fff", border: "2px solid #f8fafc", boxShadow: "0 2px 5px #173f4220", color: "#42666e" }}>
                    <GppGoodOutlinedIcon aria-label="Password required" sx={{ fontSize: 15 }} />
                  </Box>}
                </Box>
                <Typography component="span" sx={{ display: "block", fontSize: 12, fontWeight: 600, lineHeight: 1.5, overflowWrap: "anywhere" }}>{label}</Typography>
              </Button>
            })}
          </Box>
        </Box>)}
      </Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 2.5, py: 1.25, bgcolor: "#f8fafb", borderTop: "1px solid #edf1f3", color: "#60757c" }}>
        <GppGoodOutlinedIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">A shield indicates that your account password is required.</Typography>
      </Stack>
    </Box>
  )
}
